import {
  AssignmentTurnedIn,
  AutoAwesome,
  Campaign,
  BarChart,
  Business,
  CalendarMonth,
  Description,
  Folder,
  Home,
  Groups,
  Insights,
  MenuBook,
  Payments,
  People,
  Security,
  Settings,
  School,
  SupportAgent,
  Inventory2,
  Handshake,
  TaskAlt,
  Work,
} from "@mui/icons-material";

export const HELP_CLOSE_EVENT = "workspace:close-help";
export const LYRICLIOUS_THEME = "#7c3aed";

export const generalItems = [];

export const navItems = [
  { label: "Home", icon: Home },
  { label: "My Team", icon: Groups },
  { label: "Work", icon: TaskAlt },
  { label: "People", icon: People },
  { label: "Schedule", icon: CalendarMonth },
  { label: "Training", icon: School },
  { label: "Analytics", icon: BarChart },
  { label: "Administration", icon: Security },
  { label: "Projects", icon: Folder, sidebarHidden: true },
  { label: "CRM", icon: Groups, comingSoon: true },
  { label: "Knowledge", icon: MenuBook },
  { label: "Finance", icon: Payments, comingSoon: true },
  { label: "Marketing", icon: Campaign, comingSoon: true },
  { label: "AI Tools", icon: AutoAwesome, comingSoon: true },
  { label: "Procurement", icon: Business, comingSoon: true },
  { label: "Assets & Inventory", icon: Inventory2, comingSoon: true },
  { label: "Client Admin", icon: Handshake },
  { label: "Client Payments", icon: Payments },
  { label: "Client Marketing", icon: Campaign, comingSoon: true },
];

export const moduleTabs = {
  Home: ["Overview", "My Work", "Calendar", "Approvals", "Activity"],
  "My Team": ["Team", "Capacity", "Clients", "Activity"],
  Work: ["Overview", "Board", "Tickets", "Labels", "Backlog", "Sprints", "Epics", "Approvals", "Automation", "Templates"],
  People: ["Overview", "Directory", "Organization", "Departments", "Teams", "Attendance", "Leaves", "Performance", "Assets", "Documents"],
  Projects: ["Overview", "Active", "Planning", "On Hold", "Completed", "Milestones", "Settings"],
  CRM: ["Overview", "Pipeline", "Leads", "Clients", "Contacts", "Deals", "Follow Ups", "Contracts"],
  Schedule: ["Calendar", "Bookings", "Availability", "Rooms", "Requests"],
  Knowledge: ["Overview", "Documents", "SOPs", "Policies", "Templates", "Client Docs", "Project Docs", "Wiki", "Versions"],
  Finance: ["Overview", "Invoices", "Payments", "Expenses", "Payouts", "Commissions", "Subscriptions", "Payroll"],
  Training: ["Overview", "Requests", "Engagements", "Sessions", "Content Library", "Feedback & Outcomes"],
  Marketing: ["Overview", "Campaigns", "Email Marketing", "WhatsApp Campaigns", "SMS Campaigns", "Audience & Contacts", "Templates", "Consent & Preferences", "Automations", "Deliverability", "Analytics", "Blogs", "Landing Pages", "SEO", "Coupons", "Forms"],
  "AI Tools": ["Overview", "Assistant", "Workflows", "Insights"],
  "Support Desk": ["Overview", "Inbox", "Tickets", "SLA", "Knowledge Base"],
  Procurement: ["Overview", "Requests", "Purchase Orders", "Vendors", "Approvals"],
  "Assets & Inventory": ["Overview", "Assets", "Inventory", "Assignments", "Maintenance"],
  "Client Admin": ["Overview", "Projects", "Requests", "Deliverables", "Approvals", "Documents", "Team & Access", "Activity"],
  "Client Payments": ["Overview", "Invoices", "Payments", "Statements", "Billing Contacts"],
  "Client Marketing": ["Overview", "Client Accounts", "Plans & Pricing", "Usage", "Invoices", "Payments", "Branding", "Reports"],
  Analytics: ["Overview", "Productivity", "Projects", "People", "CRM", "Training", "Finance", "Marketing", "Exports"],
  Administration: ["Overview", "Workspace", "Workflows", "Users", "Roles", "Permissions", "Integrations", "Security", "Audit Logs", "Billing", "Branding", "Automation"],
  Settings: ["Account", "Profile", "Preferences", "Notifications", "Appearance", "Privacy", "Sessions"],
};

export const moduleIcons = {
  Home,
  Schedule: CalendarMonth,
  Work,
  People: Groups,
  CRM: Business,
  Knowledge: Description,
  Forms: AssignmentTurnedIn,
  Finance: Payments,
  Marketing: Campaign,
  Analytics: Insights,
  Administration: Security,
  Settings,
  Support: SupportAgent,
  Documentation: Description,
};

export const documentationItems = [
  "Product Blueprint",
  "Dashboard",
  "My Work",
  "Work",
  "Projects",
  "Sprints",
  "Meetings",
  "People",
  "CRM",
  "Attendance",
  "Docs",
  "Finance",
  "Reports",
  "Settings",
  "Company Onboarding",
  "Relationships",
  "Roles & Permissions",
  "Work Taxonomy",
  "SSO & Import",
  "Branding",
  "Automations",
];

export const dashboardTabCopy = {
  Overview: {
    title: "Company operating overview",
    text: "A clean daily snapshot across projects, people, clients, money, and work risk.",
    help: [
      "Overview is the default management dashboard. It should answer what needs attention today without forcing the user to open every module.",
      "Use this for company-wide metrics, focus items, tracker signals, risk summaries, and quick actions.",
    ],
  },
  "My Activity": {
    title: "Personal work and attendance",
    text: "Tasks, meetings, check-ins, leave, wallet, documents, and work summaries for the signed-in user.",
    help: [
      "My Activity is employee-focused. It should show only what belongs to the logged-in person.",
      "This area can pull assigned tasks, tickets, meetings, attendance status, leave requests, docs, wallet credits, and daily summaries.",
    ],
  },
  "Team Activity": {
    title: "Team presence and workload",
    text: "Managers see who is working, blocked, late, overloaded, or waiting for review.",
    help: [
      "Team Activity is for managers and admins. It gives a real-time view of team presence, workload, blockers, and capacity.",
      "This should later connect with tracker signals, attendance, project allocation, and team performance reports.",
    ],
  },
  Approvals: {
    title: "Pending decisions",
    text: "Leave, attendance corrections, client approvals, wallet payouts, and blocked work that needs action.",
    help: [
      "Approvals centralizes decisions that otherwise get lost across modules.",
      "Examples include leave approvals, attendance corrections, client milestone approvals, finance payouts, role changes, and blocked task escalations.",
    ],
  },
  Analytics: {
    title: "Business health analytics",
    text: "Trends for delivery, attendance, CRM, finance, training, and productivity.",
    help: [
      "Analytics should convert daily activity into company health signals.",
      "It can combine delivery health, utilization, attendance quality, lead conversion, finance, training, and custom reports.",
    ],
  },
};

export const workspaceStatuses = ["Planned", "Active", "Review", "Blocked", "Done"];
export const workspacePriorities = ["Low", "Medium", "High", "Urgent"];
export const workspaceOwners = ["Nisha Rao", "Kabir Shah", "Maya Singh", "Aarav Mehta", "Riya Kapoor"];

export const moduleNouns = {
  Home: "personal item",
  Schedule: "event",
  Projects: "project",
  Work: "work item",
  People: "person",
  CRM: "lead",
  Training: "training item",
  Knowledge: "document",
  Finance: "finance record",
  "Client Payments": "client payment record",
  "Client Marketing": "reseller record",
  Marketing: "campaign",
  Analytics: "dashboard",
  Administration: "setting",
};

export const statusNext = {
  Planned: "Active",
  Active: "Review",
  Review: "Done",
  Blocked: "Active",
  Done: "Active",
};

export const documentationId = (label) =>
  `documentation-${label.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;

export const getTodayValue = () => new Date().toISOString().slice(0, 10);

export const closeWorkspaceHelp = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(HELP_CLOSE_EVENT));
  }
};
